import { useRecoilCallback } from "recoil";
import {
  AppToast,
  appToastsState,
} from "@/features/line-sheet-sets/recoils/app-toasts-state";
import { uniqueId } from "lodash";
import { useCallback, useEffect, useRef } from "react";
import { isErrorResponse } from "@/utils/use-fetcher";
import { ValidationError } from "yup";
import { toLower } from "@/utils/case";
import useMemoizedValue from "@/features/ui/hooks/use-memoized-value";
import { produce } from "immer";
import { useToast } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  ErrorResponse,
  isLocalizedMessage,
  LocalizedMessage,
} from "@/utils/uno-axios";
import { TFunction } from "i18next/typescript/t";
import { i18n } from "i18next";
import { reportError } from "@/utils/sentry-report";
import { toErrorResponse } from "@/utils/axios-client-error";

export function isValidationError(obj: any): obj is ValidationError {
  return (obj as ValidationError).inner != undefined;
}

interface ErrorOption {
  id?: string;
  i18n?: {
    keyPrefix: string;
    ns?: string;
  };
  // 서버가 준 실패 사유를 안내 문구 아래 보조 줄로 함께 보여줄지.
  // 기본은 끔 — 서버 원문이 사용자에게 그대로 보이면 안 되는 화면이 많다.
  withServerMessage?: boolean;
}

function calcErrorResponseMessage(
  response: ErrorResponse,
  t: TFunction<"translation", undefined>,
  i18n: i18n,
  option?: ErrorOption
) {
  if (option?.i18n) {
    const { keyPrefix, ns } = option.i18n;
    if (keyPrefix) {
      const key1 = `${keyPrefix}.${response.error.code}.${toLower(
        response.error.status
      )}`;

      const key2 = `${keyPrefix}.${response.error.code}`;

      const keys = [key1, key2];
      const key = keys.find((i) => i18n.exists(i, { ns: ns || "common" }));

      if (key) {
        return t(key, { ns: ns || "common" });
      }
    }
  }

  if (response.error.details && response.error.details.length > 0) {
    const myLng = i18n.resolvedLanguage || "en";
    const localizedMessage = response.error.details.find((detail) => {
      if (isLocalizedMessage(detail)) {
        const lng = detail.message.split("-")[0];
        return myLng === lng;
      }
    }) as LocalizedMessage | undefined;

    if (localizedMessage) {
      return localizedMessage.message;
    }
  }

  if (response.error.message.length > 0) {
    return response.error.message;
  }

  const key = `message.error.http.${response.error.code}`;
  if (i18n.exists(key)) {
    return t(key);
  }
}

export default function useAppToasts(props?: ErrorOption) {
  const { t, i18n } = useI18nHelper();
  const tRef = useRef(t);
  const i18nRef = useRef(i18n);
  tRef.current = t;
  i18nRef.current = i18n;

  const toast = useToast();

  const defaultOption = useMemoizedValue(props);

  const remove = useRecoilCallback(
    ({ set }) =>
      (id: string) => {
        if (toast.isActive(id)) {
          toast.close(id);
        }

        set(appToastsState, (prev) => {
          return prev.filter((i) => i.id !== id);
        });
      },
    [toast]
  );

  const add = useRecoilCallback(
    ({ set }) =>
      (appToast: Omit<AppToast, "id"> & { id?: string }) => {
        set(appToastsState, (prev) => {
          const id = appToast.id || uniqueId("appToast_");

          return produce(prev, (draft) => {
            const index = draft.findIndex((i) => i.id === id);
            if (index === -1) {
              draft.push({ ...appToast, id });
            } else {
              draft[index] = { ...appToast, id };
            }
          });
        });
      },
    []
  );

  const error = useCallback(
    (error: any, option?: ErrorOption) => {
      if (error) {
        const t = tRef.current;
        const i18n = i18nRef.current;
        let message;

        // 안내 문구 아래에 덧붙이는 보조 줄 — 켜 둔 호출부에서 원인을 알 수 있을 때만 채운다.
        let description;
        // 해석 순서는 id와 같다 — 호출 단위 옵션이 훅 단위 기본값을 덮는다.
        const withServerMessage =
          option?.withServerMessage ??
          defaultOption?.withServerMessage ??
          false;

        if (isErrorResponse(error)) {
          message = calcErrorResponseMessage(error, t, i18n, option);
        } else if (isValidationError(error) && error.inner.length > 0) {
          const first = error.inner.find((i) => i.errors.length > 0);
          if (first) {
            message = first.errors[0];
          }
        }

        if (!message) {
          message = t("message.error.unknown");

          if (withServerMessage) {
            // axios-client 경로는 응답 인터셉터에서 error.response.data를 벗기지 않아
            // 위 isErrorResponse 가드를 통과하지 못한다. 켜 둔 호출부에서만 서버가 준
            // 문구를 안내 문구 아래에 덧붙여 원인을 볼 수 있게 한다.
            const fromServer = calcErrorResponseMessage(
              toErrorResponse(error),
              t,
              i18n,
              option
            );
            // 같은 문구가 두 줄로 겹치는 경우(폴백이 안내 문구로 되돌아온 경우)는 버린다.
            description = fromServer !== message ? fromServer : undefined;
          }

          // 서버 문구를 얻었다고 예상한 에러가 되는 건 아니라 리포트는 플래그와 무관하게 남긴다.
          // 오류가 아닌 값(서버 오류 응답·직접 던진 객체)은 그대로 보내면 "[object Object]" 로만 찍혀
          // 원인이 사라진다 — 내용을 풀어 감싸서 보낸다(utils/sentry-report).
          reportError(error, { source: "app-toast" });
        }

        if (message.length > 0) {
          add({
            status: "error",
            message,
            description,
            id: option?.id || defaultOption?.id,
          });
        }
      }
    },
    [add, defaultOption]
  );

  const warning = useCallback(
    (message: string, id?: string) => {
      add({ status: "warning", message, id: id || defaultOption?.id });
    },
    [add, defaultOption]
  );

  const info = useCallback(
    (message: string, id?: string) => {
      add({ status: "success", message, id: id || defaultOption?.id });
    },
    [add, defaultOption]
  );

  const close = useCallback(
    (id?: string) => {
      const actualId = id || defaultOption?.id;
      if (actualId) {
        remove(actualId);
      }
    },
    [remove, defaultOption?.id]
  );

  useEffect(() => {
    if (defaultOption?.id) {
      return () => {
        if (defaultOption?.id) {
          remove(defaultOption.id);
        }
      };
    }
  }, [defaultOption?.id, remove]);

  return { remove, add, error, warning, info, close };
}

/**
 * SWR error를 안전하게 토스트로 보여주는 훅.
 * 동일한 error 객체(캐시 재사용)는 중복 표시하지 않음.
 */
export function useShowError(swrError: any, showError: (e: any) => void) {
  const shownErrorRef = useRef<any>(undefined);

  useEffect(() => {
    if (swrError && swrError !== shownErrorRef.current) {
      shownErrorRef.current = swrError;
      showError(swrError);
    }
  }, [swrError, showError]);
}
